import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { BadgeDollarSign, Check, Loader2, Search } from "lucide-react";
import {
  getProductAffiliates,
  updateProductAffiliates,
  type AffiliateProvider,
  type ProductAffiliateConfig,
} from "../../services/AppConfigService";

const QUERY_KEY = ["product-affiliates"] as const;

const providerBadgeClasses: Record<Exclude<AffiliateProvider, "">, string> = {
  amazon: "bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300",
  impact: "bg-violet-100 text-violet-800 dark:bg-violet-950/50 dark:text-violet-300",
  mavely: "bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-300",
};

export default function ProductAffiliatesTab() {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<ProductAffiliateConfig[]>([]);
  const [search, setSearch] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: getProductAffiliates,
  });

  useEffect(() => {
    if (!data) return;
    setRows(data.affiliates);
    setDirty(false);
  }, [data]);

  const mutation = useMutation({
    mutationFn: updateProductAffiliates,
    onSuccess: (response) => {
      queryClient.setQueryData(QUERY_KEY, response);
      setRows(response.affiliates);
      setDirty(false);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    },
  });

  const updateRow = (retailer: string, change: Partial<ProductAffiliateConfig>) => {
    setRows((current) => current.map((row) => (
      row.retailer === retailer ? { ...row, ...change } : row
    )));
    setDirty(true);
    setSaved(false);
  };

  const handleProviderChange = (row: ProductAffiliateConfig, provider: AffiliateProvider) => {
    updateRow(row.retailer, {
      provider,
      enabled: provider === "" ? false : row.enabled,
    });
  };

  const normalizedSearch = search.trim().toLowerCase();
  const visibleRows = useMemo(() => rows.filter((row) => (
    !normalizedSearch
    || row.displayName.toLowerCase().includes(normalizedSearch)
    || row.retailer.toLowerCase().includes(normalizedSearch)
    || row.provider.toLowerCase().includes(normalizedSearch)
  )), [normalizedSearch, rows]);

  const configuredCount = rows.filter((row) => row.provider !== "").length;
  const enabledCount = rows.filter((row) => row.enabled && row.provider !== "").length;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-5 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300">
        Product affiliate configuration could not be loaded.
      </div>
    );
  }

  return (
    <div className="max-w-5xl">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BadgeDollarSign className="w-4 h-4 text-gray-500 dark:text-dark-400" />
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Product Affiliates</h2>
          </div>
          <p className="max-w-2xl text-xs leading-5 text-gray-500 dark:text-dark-400">
            Choose the affiliate provider for each retailer and control whether it is active. Empty providers use a protected direct retailer redirect.
          </p>
        </div>

        <div className="flex gap-2 text-xs shrink-0">
          <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">
            {enabledCount} enabled
          </span>
          <span className="rounded-full bg-gray-100 px-2.5 py-1 font-semibold text-gray-600 dark:bg-dark-800 dark:text-dark-300">
            {configuredCount} configured
          </span>
        </div>
      </div>

      {data.configMissing && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-300">
          Showing the current built-in affiliate defaults. Save changes to create the product-affiliates app_config record.
        </div>
      )}

      <div className="relative mb-3">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search retailers or providers"
          className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-red-400 focus:ring-2 focus:ring-red-100 dark:border-dark-700 dark:bg-dark-900 dark:text-white dark:focus:border-red-500 dark:focus:ring-red-950"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-dark-700 dark:bg-dark-900">
        <div className="hidden grid-cols-[minmax(180px,1fr)_220px_100px] gap-4 border-b border-gray-200 bg-gray-50 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.14em] text-gray-400 dark:border-dark-700 dark:bg-dark-800/70 dark:text-dark-400 sm:grid">
          <span>Retailer</span>
          <span>Affiliate provider</span>
          <span className="text-right">Enabled</span>
        </div>

        <div className="divide-y divide-gray-100 dark:divide-dark-800">
          {visibleRows.map((row) => (
            <div key={row.retailer} className="grid gap-3 px-4 py-3 sm:grid-cols-[minmax(180px,1fr)_220px_100px] sm:items-center sm:gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">{row.displayName}</span>
                  {row.provider && (
                    <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${providerBadgeClasses[row.provider]}`}>
                      {row.provider}
                    </span>
                  )}
                </div>
                <span className="font-mono text-[10px] text-gray-400 dark:text-dark-500">{row.retailer}</span>
              </div>

              <label className="sm:block">
                <span className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-gray-400 sm:hidden">Affiliate provider</span>
                <select
                  aria-label={`${row.displayName} affiliate provider`}
                  value={row.provider}
                  onChange={(event) => handleProviderChange(row, event.target.value as AffiliateProvider)}
                  className="w-full rounded-lg border border-gray-200 bg-white px-2.5 py-2 text-xs font-medium text-gray-700 outline-none focus:border-red-400 focus:ring-2 focus:ring-red-100 dark:border-dark-700 dark:bg-dark-950 dark:text-dark-200 dark:focus:border-red-500 dark:focus:ring-red-950"
                >
                  <option value="">No affiliate provider</option>
                  {data.providers.map((provider) => (
                    <option key={provider.type} value={provider.type}>{provider.label}</option>
                  ))}
                </select>
              </label>

              <div className="flex items-center justify-between sm:justify-end">
                <span className="text-[10px] font-bold uppercase tracking-wide text-gray-400 sm:hidden">Enabled</span>
                <button
                  type="button"
                  role="switch"
                  aria-label={`${row.displayName} affiliate enabled`}
                  aria-checked={row.enabled}
                  disabled={!row.provider}
                  onClick={() => updateRow(row.retailer, { enabled: !row.enabled })}
                  className={`relative h-6 w-11 rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${
                    row.enabled ? "bg-emerald-500" : "bg-gray-300 dark:bg-dark-600"
                  }`}
                >
                  <span className={`absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${row.enabled ? "translate-x-5" : "translate-x-0"}`} />
                </button>
              </div>
            </div>
          ))}

          {visibleRows.length === 0 && (
            <p className="px-4 py-10 text-center text-sm text-gray-400">No retailers match that search.</p>
          )}
        </div>
      </div>

      <div className="sticky bottom-3 mt-4 flex items-center gap-3 rounded-xl border border-gray-200 bg-white/95 px-3 py-3 shadow-lg backdrop-blur dark:border-dark-700 dark:bg-dark-900/95">
        <button
          type="button"
          onClick={() => mutation.mutate(rows)}
          disabled={!dirty || mutation.isPending}
          className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : saved ? <Check className="h-3.5 w-3.5" /> : null}
          {saved ? "Saved" : "Save Changes"}
        </button>
        {dirty && !mutation.isPending && <span className="text-xs text-amber-600 dark:text-amber-400">Unsaved changes</span>}
        {mutation.isError && <span className="text-xs text-red-600 dark:text-red-400">Failed to save changes</span>}
      </div>
    </div>
  );
}
